import React, { useState } from 'react';
import { Alert, Button, CircularProgress, Grid } from '@mui/material';
import { Field, InjectedFormProps, reduxForm } from 'redux-form';
import { useTypedSelector } from 'hooks/use-typed-selector';
import { renderDropdownNonSimple } from 'components/form/field/redux-field';
import { required } from 'components/form/validation/validation';
import { StringOption } from 'interfaces/forms/types';
import { UserPermissions } from 'interfaces/user/Permissions';
import SelectFileForm from 'components/document/file-forms/select-file-form';
import CustomerDropdown from '../components/customer-dropdown';
import { SectionTitleStyled } from '../components/section-title';
import { FILE_FREQUENCY_LABELS, FILE_TYPE_LABELS, FileFrequencyEnum, FileTypeEnum } from './types';
import { createPresignedUpload, uploadFileToS3 } from './file-import-service';

const FORM_NAME = 'fileImportUpload';

interface IFormValues {
  file_type: StringOption;
  frequency: StringOption;
}

const FILE_TYPE_OPTIONS: StringOption[] = Object.values(FileTypeEnum).map(value => ({
  label: FILE_TYPE_LABELS[value],
  value,
}));

const FREQUENCY_OPTIONS: StringOption[] = Object.values(FileFrequencyEnum).map(value => ({
  label: FILE_FREQUENCY_LABELS[value],
  value,
}));

const FileImportUpload = (props: InjectedFormProps<IFormValues>): JSX.Element => {
  const { handleSubmit, reset } = props;

  const sessionCustomerId = useTypedSelector(state => state.filters.selectedCustomerId);

  const [selectedCustomerId, setSelectedCustomerId] = useState<number>(sessionCustomerId ?? 0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleAcceptedDrop = (files: File[] | FileList) => {
    const file = Array.isArray(files) ? files[0] : files[0];
    setSelectedFile(file ?? null);
    setFileError(null);
    setUploadSuccess(false);
    setUploadError(null);
  };

  const handleRejectedDrop = () => {
    setSelectedFile(null);
    setFileError('Invalid file. Please select a CSV or TXT file.');
  };

  const handleFilePaste = (e: React.ClipboardEvent) => {
    if (e?.clipboardData?.files?.length > 0) {
      handleAcceptedDrop(e.clipboardData.files);
    } else {
      handleRejectedDrop();
    }
  };

  const onSubmit = async (values: IFormValues) => {
    if (!selectedFile) {
      setFileError('Please select a file to upload.');
      return;
    }
    if (!selectedCustomerId) {
      return;
    }

    setUploading(true);
    setUploadSuccess(false);
    setUploadError(null);

    try {
      const presigned = await createPresignedUpload({
        file_name: selectedFile.name,
        customer_id: selectedCustomerId,
        file_type: values.file_type.value as FileTypeEnum,
        frequency: values.frequency.value as FileFrequencyEnum,
      });

      await uploadFileToS3(presigned.url, presigned.fields, selectedFile);

      setUploadSuccess(true);
      setSelectedFile(null);
      reset();
    } catch {
      setUploadError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const isSubmitDisabled = !selectedCustomerId || !selectedFile || uploading;

  return (
    <>
      <SectionTitleStyled title="Upload Dispense File" />
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <Grid container direction="column" spacing={2} sx={{ maxWidth: 600 }}>
          <Grid item xs={12}>
            <CustomerDropdown
              isLabelOn
              isSelectedOption
              selectedCustomerId={selectedCustomerId}
              setSelectedCustomerId={setSelectedCustomerId}
              requiredPermission={UserPermissions.FileImportUpload}
            />
          </Grid>
          <Grid item xs={12}>
            <Field
              name="file_type"
              label="File Type"
              component={renderDropdownNonSimple}
              fields={FILE_TYPE_OPTIONS}
              validate={[required]}
            />
          </Grid>
          <Grid item xs={12}>
            <Field
              name="frequency"
              label="Frequency"
              component={renderDropdownNonSimple}
              fields={FREQUENCY_OPTIONS}
              validate={[required]}
            />
          </Grid>
          <Grid item xs={12}>
            <SelectFileForm
              acceptedFileExtensions=".csv,.txt"
              errorMessage={fileError}
              handleAcceptedDrop={handleAcceptedDrop}
              handleRejectedDrop={handleRejectedDrop}
              handleFilePaste={handleFilePaste}
              dropAreaText={
                selectedFile
                  ? `Selected: ${selectedFile.name}`
                  : 'Drag a CSV or TXT file here, or click "Select a file" to browse.'
              }
            />
          </Grid>
          {uploadSuccess && (
            <Grid item xs={12}>
              <Alert severity="success">
                File uploaded successfully. Processing will begin shortly.
              </Alert>
            </Grid>
          )}
          {uploadError && (
            <Grid item xs={12}>
              <Alert severity="error">{uploadError}</Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitDisabled}
              startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </Grid>
        </Grid>
      </form>
    </>
  );
};

export default reduxForm<IFormValues>({ form: FORM_NAME })(FileImportUpload);
